import { hero } from '../../data/content';
import { Link } from 'react-router-dom';
import { useEmail } from '../../context/EmailContext';

export default function Hero() {
  const { handleQuoteRequest, loadingId } = useEmail();

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight - 112,
      behavior: 'smooth',
    });
  };

  return (
    <section className="relative min-h-[calc(100vh-112px)] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-primary-dark to-slate-900">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Floating molecules - left side */}
      <div className="absolute left-0 top-0 w-full h-full overflow-hidden pointer-events-none">
        {/* Large molecule structure */}
        <svg
          className="absolute -left-20 top-1/4 w-96 h-96 text-white/5 animate-pulse"
          viewBox="0 0 200 200"
          fill="currentColor"
        >
          <circle cx="100" cy="100" r="20" />
          <circle cx="50" cy="50" r="12" />
          <circle cx="150" cy="50" r="12" />
          <circle cx="50" cy="150" r="12" />
          <circle cx="150" cy="150" r="12" />
          <line x1="100" y1="100" x2="50" y2="50" stroke="currentColor" strokeWidth="3" />
          <line x1="100" y1="100" x2="150" y2="50" stroke="currentColor" strokeWidth="3" />
          <line x1="100" y1="100" x2="50" y2="150" stroke="currentColor" strokeWidth="3" />
          <line x1="100" y1="100" x2="150" y2="150" stroke="currentColor" strokeWidth="3" />
        </svg>

        {/* Benzene ring */}
        <svg
          className="absolute right-10 top-20 w-48 h-48 text-white/5"
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" />
          <polygon points="50,25 75,37.5 75,62.5 50,75 25,62.5 25,37.5" />
        </svg>

        {/* DNA helix suggestion */}
        <svg
          className="absolute -right-10 bottom-1/4 w-64 h-96 text-white/5"
          viewBox="0 0 100 200"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M30,10 Q70,50 30,90 Q-10,130 30,170 Q70,210 30,250" />
          <path d="M70,10 Q30,50 70,90 Q110,130 70,170 Q30,210 70,250" />
          {[20, 40, 60, 80, 100, 120, 140, 160, 180].map((y) => (
            <line key={y} x1="35" y1={y} x2="65" y2={y} strokeWidth="1.5" />
          ))}
        </svg>
      </div>

      {/* Floating lab equipment icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Test tube */}
        <div className="absolute left-[10%] top-[20%] animate-float-slow">
          <svg className="w-16 h-16 text-cyan-400/20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 2v2h1v14a4 4 0 0 0 8 0V4h1V2H7zm2 2h6v7H9V4zm0 9h6v5a2 2 0 0 1-4 0v-1H9v-4z" />
          </svg>
        </div>

        {/* Flask */}
        <div className="absolute right-[15%] top-[30%] animate-float-medium">
          <svg className="w-20 h-20 text-emerald-400/20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 22a3 3 0 0 0 3-3v-7.2L6.2 8H5V6h14v2h-1.2L15 11.8V19a3 3 0 0 0 3 3H6zM9 8l3 4.8L15 8H9z" />
          </svg>
        </div>

        {/* Microscope */}
        <div className="absolute left-[20%] bottom-[25%] animate-float-medium">
          <svg className="w-14 h-14 text-violet-400/20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2a2 2 0 0 1 2 2v8.5l4.5 4.5H19a2 2 0 0 1 0 4H5a2 2 0 0 1 0-4h.5L10 12.5V4a2 2 0 0 1 2-2zm0 2a.5.5 0 0 0-.5.5v8.67l-4.33 4.33H16.83l-4.33-4.33V4.5A.5.5 0 0 0 12 4z" />
          </svg>
        </div>

        {/* Atom */}
        <div className="absolute right-[8%] bottom-[35%] animate-spin-slow">
          <svg className="w-24 h-24 text-amber-400/15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <ellipse cx="12" cy="12" rx="10" ry="4" />
            <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" />
            <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" />
          </svg>
        </div>

        {/* Chromatograph peaks */}
        <div className="absolute left-[5%] bottom-[15%]">
          <svg className="w-32 h-20 text-white/10" viewBox="0 0 120 50" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M0,45 L20,45 L25,40 L30,20 L35,45 L50,45 L55,35 L60,5 L65,35 L70,45 L85,45 L90,30 L95,15 L100,30 L105,45 L120,45" />
          </svg>
        </div>
      </div>

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/30 rounded-full blur-3xl animate-pulse delay-1000" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

      {/* Main content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-sm text-gray-200 font-medium">DEA Registered • cGMP Compliant • NIST Certified</span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 leading-tight text-white">
          {hero.headline.split(',')[0]},
          <span className="block mt-2 bg-gradient-to-r from-cyan-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent">
            {hero.headline.split(',')[1]}
          </span>
        </h1>

        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
          {hero.subheadline}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/services"
            className="group relative inline-flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg shadow-white/20"
          >
            <span>Explore Our Services</span>
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <button
            onClick={() => handleQuoteRequest(undefined, 'hero')}
            disabled={loadingId === 'hero'}
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 relative"
          >
            <span className={`inline-flex items-center gap-2 ${loadingId === 'hero' ? 'invisible' : ''}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Request a Quote</span>
            </span>
            {loadingId === 'hero' && (
              <svg className="w-5 h-5 animate-spin absolute" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
          </button>
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-white mb-1">30+</div>
            <div className="text-sm text-gray-400">Years Experience</div>
          </div>
          <div className="text-center border-x border-white/20">
            <div className="text-3xl md:text-4xl font-bold text-white mb-1">95%+</div>
            <div className="text-sm text-gray-400">NIST Accuracy</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-white mb-1">7 Day</div>
            <div className="text-sm text-gray-400">Turnaround</div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - hidden on mobile */}
      <button
        onClick={scrollToContent}
        className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer bg-transparent border-0 group"
        aria-label="Scroll to content"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-gray-400 uppercase tracking-wider">Scroll</span>
          <svg
            className="w-6 h-6 text-white/60 group-hover:text-white animate-bounce transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </button>
    </section>
  );
}
